import * as React from 'react';
import { useIntl } from 'react-intl';
import { isFunction, isNumber, isNil, uniqueId, values, filter, forEach, has } from 'lodash-es';
import type { TableProps } from 'antd';
import { Table, Button } from 'antd';
import type { TablePaginationConfig, ColumnsType, ColumnType, ColumnGroupType } from 'antd/es/table';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { runInAction, observable, toJS } from 'mobx';
import { Resizable } from 'react-resizable';
import { FilterValue, SorterResult, TableCurrentDataSource } from 'antd/es/table/interface';
import { ColumnSettingsPopover } from './components/ColumnSettingsPopover';
import { MinusSquareOutlined, PlusSquareOutlined } from '@ant-design/icons';
// import classNames from 'classnames';

interface TableSortedInfo {
  order: string | 'descend' | 'ascend',
  columnKey: string
}
export interface ColumnsSettingsItemType {
  title: string,
  key: string,
  fixed: boolean,
  visible: boolean,
  expand?: boolean,
  isExpandChild?: boolean,
  sortIndex?: number,
}
export interface DataTableColumnType<RecordType> extends ColumnType<RecordType> {
  hideInSettings?: boolean; // 在设置中隐藏
  visible?: boolean; // 默认显示/隐藏
  expand?: boolean; //默认展开/收起
  hideExpend?: boolean;//默认显示 展开/收起按钮  为true隐藏
}
export type DataTableColumnsType<RecordType = unknown> = (ColumnGroupType<RecordType> | DataTableColumnType<RecordType>)[];
export interface TableRefInstance<RecordType = unknown> {
  pagination?: false | TablePaginationConfig;
  dataSource?: any[],
  deferLoading?: boolean;
  action?: (data?: any) => Promise<any>;
  requestParams?: (() => object) | object;
  columns: DataTableColumnsType<RecordType>;
  tableId?: string;
}

export interface DataTableApiInstance<RecordType = unknown> {
  pagination: TablePaginationConfig;
  dataSource?: any[];
  tableRef: React.RefObject<TableRefInstance<RecordType> | null>;
  requestId: string;
  loading: boolean;
  readonly serverMode: boolean;
  /**
   *
   * @param {number | 'page' | 'reset' | true} paging
   * paging = 'reset' | true (default 默认值) 分页重置
   * paging = 'page' 分页保持不变
   * paging = number 自定义分页
   */
  request: (paging?: number | 'page' | 'reset' | true) => Promise<void>;
  registerTable: (ref: React.RefObject<TableRefInstance<any>>) => void;
  cancelRegisterTable: () => void;
  onChange: (page: number, pageSize: number) => void;
  onShowSizeChange: (current: number, pageSize: number) => void;
  /**
   * 终止请求方法
   */
  _abort: Function;
  /**
   * 是否在 DataTable 挂载前发起请求
   */
  _isRequestBeforeMounted: boolean;
  //表头字段数据排序-服务端
  tableSortedInfo: TableSortedInfo;
  onTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<any> | SorterResult<any>[],
    extra: TableCurrentDataSource<any>
  ) => void;
  //列设置
  columnsSettings: object[];
  tableId?: string;
  setTableId: (tableId: any) => void;
  setColumnsSettings: (settings: any) => void;
  getColumnsSettings: () => ColumnsSettingsItemType[];
  getDefaultColumnsSettings: () => ColumnsSettingsItemType[];
  changeExpandColumn: (key: string) => void;
}

export function useTable(table?: DataTableApiInstance | undefined): [DataTableApiInstance] {
  const store: DataTableApiInstance = useLocalObservable(() => (table ?? {
    pagination: {
      current: 1,
      pageSize: 20,
      total: 0,
      pageSizeOptions: [10, 20, 50, 100, 300, 500]
    },
    dataSource: [],
    loading: false,
    tableRef: { current: null } as React.RefObject<any>,
    requestId: uniqueId(),
    tableSortedInfo: {
      order: '',
      columnKey: ''
    },
    _abort: () => { },
    _isRequestBeforeMounted: false,
    onChange(page: number, pageSize: number) {
      if (this.serverMode) {
        this.request(page);
      }
      this.tableRef.current?.pagination?.onChang?.(page, pageSize);
    },
    onTableChange(pagination: any, filters: any, sorter: any) {
      const isSorter = (this.tableRef?.current?.columns || []).filter((d: any) => {
        return d?.dataIndex == sorter?.field || d?.key == sorter?.field;
      })[0]?.sorter;
      if ((sorter?.column?.sorter === true || isSorter)
        && ( sorter?.columnKey !== this.tableSortedInfo.columnKey
        || sorter?.order !== this.tableSortedInfo.order )
      ) {
        this.tableSortedInfo = {
          order: sorter.order,
          columnKey: sorter.columnKey
        };
        this.request();
      }
    },
    onShowSizeChange(current: number, pageSize: number) {
      if (this.serverMode) {
        if (this.pagination) {
          this.pagination.pageSize = pageSize;
        }
        this.request();
      }
      this.tableRef.current?.pagination?.onShowSizeChange(current, pageSize);
    },
    get serverMode(): boolean {
      return !isNil(this.tableRef.current?.action);
    },
    registerTable(ref: React.RefObject<any>) {
      this.tableRef = ref;
      ['pageSize', 'pageSizeOptions'].forEach((key) => {
        if (ref.current?.pagination?.[key]) {
          this.pagination[key] = ref.current?.pagination?.[key];
        }
      });
      this.tableId = ref.current?.tableId ?? '';
      // 保存sorter默认值
      let defSort: any;
      forEach(ref.current?.columns, (o) => {
        o ??= {};
        defSort = o.defaultSortOrder ? o : defSort;
        defSort = filter(o?.children, 'defaultSortOrder')?.[0] || defSort;
      });
      if (defSort?.sorter) {
        this.tableSortedInfo = {
          order: defSort?.defaultSortOrder,
          columnKey: defSort?.key
        };
      } else {
        this.tableSortedInfo = {
          order: '',
          columnKey: ''
        };
      }
    },
    cancelRegisterTable() {
      this.tableRef = { current: null };
      this._isRequestBeforeMounted = false;
    },
    async request(paging = 'reset') {
      /** DataTable 挂载前发起请求时，延时请求等挂载后再请求 */
      if (!this.tableRef.current) {
        this._isRequestBeforeMounted = true;
        return;
      }

      const isPagination = this.tableRef.current.pagination !== false;
      if (isPagination && paging != 'page') {
        this.pagination.current = paging == 'reset' || paging === true
          ? 1
          : isNumber(paging)
            ? paging
            : 1;
      }
      if (isFunction(this.tableRef.current?.action)) {
        const currentRequestId = uniqueId();
        this.loading = true;
        this.requestId = currentRequestId;
        this._abort();
        const result = await this.tableRef.current?.action({
          sEcho: currentRequestId,
          ...(
            isPagination
              ? {
                iDisplayStart: ((this.pagination?.current ?? 0) - 1)
                  * (this.pagination?.pageSize ?? 20),
                iDisplayLength: this.pagination?.pageSize ?? 20,
              }
              : null
          ),
          ...(
            values(this.tableSortedInfo).length
              ? {
                aSorting: [{
                  sName: this.tableSortedInfo.columnKey,
                  sValue: this.tableSortedInfo.order === 'descend'
                    ? 'desc'
                    : this.tableSortedInfo.order === 'ascend'
                      ? 'asc'
                      : this.tableSortedInfo.order,
                }]
              }
              : null
          ),
          ...(
            (
              isFunction(this.tableRef.current?.requestParams)
                ? this.tableRef.current?.requestParams(toJS(this.pagination),toJS(this.tableSortedInfo))
                : this.tableRef.current?.requestParams
            ) ?? null
          ),
        }, {
          getAbort: (fun: Function) => this._abort = fun,
        }).finally(() => {
          if (this.requestId == currentRequestId) {
            runInAction(() => this.loading = false);
          }
        });
        const {
          aoData,
          iTotalDisplayRecords,
          iTotalRecords,
          sEcho,
        } = this.tableRef.current?.convertServerData
          ? this.tableRef.current?.convertServerData(result)
          : result;

        // 请求时序不对，不做数据处理
        if (sEcho && sEcho != this.requestId) {
          return;
        };

        //更新分页和数据
        runInAction(() => {
          this.dataSource = aoData || [];
          if (this.pagination) {
            this.pagination = {
              ...this.pagination,
              total: iTotalDisplayRecords ?? 0,
              pageSize: iTotalRecords && isNumber(iTotalRecords)
                ? iTotalRecords
                : this.pagination.pageSize,
            };
          }
        });

      }
    },
    tableId: '',
    columnsSettings: [],
    setTableId(tableId) {
      if (tableId) {
        this.tableId = tableId;
      }
    },
    getDefaultColumnsSettings() {
      return (this.tableRef.current?.columns ?? [])
        .filter((column: any) =>
          column.hideInSettings !== true
          && !column.fixed
          && (column.key || column.dataIndex)
          && column.title
        )
        // columns.children也显示在配置里
        .reduce((arr: any[], column: any, i: number) => {
          let curItem: ColumnsSettingsItemType = {
            title: column.title,
            key: column.key ?? column.dataIndex,
            fixed: Boolean(column.fixed),
            visible: column.visible !== false,
            sortIndex: i
          };
          let curChildItem = [];
          if (column.children?.length) {
            curItem.expand = column.expand !== false;
            curChildItem = column.children.map((childColumn: any) => ({
              title: childColumn.title,
              key: childColumn.key ?? childColumn.dataIndex,
              fixed: Boolean(childColumn.fixed),
              visible: childColumn.visible !== false,
              isExpandChild: true,
            }));
          }
          arr.push(curItem);
          arr = arr.concat(curChildItem);
          return arr;
        }, []);
    },
    getColumnsSettings() {
      const columnsSettingsMap = [...this.columnsSettings].reduce((map, d: any) => {
        map[d.key] = d;
        return map;
      }, {});
      return this.getDefaultColumnsSettings()
        .map((column: any, i: number) => {
          column ??= {};
          const setting = columnsSettingsMap[column.key ?? column.dataIndex];
          let columnItem: ColumnsSettingsItemType = {
            title: column.title,
            key: column?.key ?? column?.dataIndex,
            fixed: setting?.fixed ?? Boolean(column.fixed),
            visible: setting?.visible ?? column.visible !== false,
            sortIndex: isNumber(setting?.sortIndex) ? setting?.sortIndex : i,
            isExpandChild: column?.isExpandChild,
          };
          if (has(column, 'expand')) {
            columnItem.expand = setting?.expand ?? column.expand !== false;
          }
          return columnItem;
        });
    },
    setColumnsSettings(settings: object[]) {
      this.columnsSettings = settings;
    },
    changeExpandColumn(key: string) {
      let columnsSettings = this.getColumnsSettings().map((d: any) => {
        if (d.key === key) {
          d.expand = !d.expand;
        }
        return d;
      });
      this.setColumnsSettings(columnsSettings);
    },
  }), {
    dataSource: observable.shallow,
    pagination: observable.shallow,
    tableRef: observable.ref,
    requestId: false,
    columnsSettings: observable.shallow,
  });
  return [store];
}

export type DataTableServerDataFormat = {
  aoData: [];
  /**
   * 数据总数
   */
  iTotalDisplayRecords?: number;
  /**
   * 分页数
   */
  iTotalRecords?: number;
  /**
   * 请求 uid
   */
  sEcho?: number;
}

export interface DataTableProps<RecordType>
  extends TableProps<RecordType> {
  table?: DataTableApiInstance;
  deferLoading?: boolean;
  action?: (data?: any) => Promise<any>;
  requestParams?: ((pagination: TablePaginationConfig,sortInfo:TableSortedInfo) => object) | object;
  convertServerData?: (serverData: any) => DataTableServerDataFormat;
  tableId?: string;
  columns?: DataTableColumnsType<RecordType>;
  disabledResize?: boolean | undefined;
}

type ResizableTitleProps = {
  onResize: ((e: React.SyntheticEvent) => any) | undefined;
  width: number | undefined,
  disabledResize?: boolean | undefined;
};
const ResizableTitle = (props: ResizableTitleProps) => {
  const { onResize, width, disabledResize, ...restProps } = props;

  if (!width || typeof width != 'number' || disabledResize) {
    return <th {...restProps} />;
  }

  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-table-handle"
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
      }
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps} />
    </Resizable>
  );
};

const DataTable = observer(React.forwardRef<HTMLDivElement, DataTableProps<any>>(function <RecordType extends object = any>(
  props: DataTableProps<RecordType>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const {
    pagination,
    dataSource,
    table,
    deferLoading = false,
    action,
    requestParams,
    convertServerData,
    scroll = { x: 'max-content' },
    loading,
    components = {},
    columns = [],
    onChange,
    tableId,
    // sticky,
    // className,
    ...rest
  } = props;
  const [store] = useTable(table);
  const intl = useIntl();

  /** 将 DataTable props 关联到 store */
  const tablePropRef = React.useRef<TableRefInstance<RecordType> | null>(null);
  React.useImperativeHandle(tablePropRef, () => ({
    action,
    pagination,
    deferLoading,
    requestParams,
    dataSource: dataSource as RecordType[],
    convertServerData,
    onChange,
    columns,
    tableId,
  }));
  React.useEffect(() => {
    store.registerTable(tablePropRef);
    return store.cancelRegisterTable;
  }, []);
  React.useEffect(() => {
    store.setTableId(tableId);
  }, [tableId]);

  /** 是否服务请求 */
  const tableControlledProps = store.serverMode
    ? {
      pagination: pagination !== false
        ? {
          ...pagination,
          ...store.pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total: number, range: number[]) => intl.formatMessage({
            defaultMessage: '{start} 至 {end}， 共 {total} 条记录',
          }, { total, start: range[0], end: range[1] }),
          onChange: store.onChange,
          onShowSizeChange: store.onShowSizeChange,
        }
        : pagination,
      dataSource: store.dataSource as RecordType[],
      loading: store.loading || loading,
      onChange: store.onTableChange,
    }
    : {
      pagination,
      dataSource,
      loading,
      onChange
    };

  /** resize columns */
  const getColumnKey = (column: any) => column?.key || column?.dataIndex;
  const [columnsMap, setColumnsMap] = React.useState(columns.reduce((m: any, col: any) => {
    const key = getColumnKey(col);
    if (key) {
      m[key] = col;
    }
    return m;
  }, {}));
  const tableComponents = Object.assign({
    header: {
      cell: ResizableTitle,
    },
  }, components);
  const handleResize = (columnKey: string) => (e: any, { size }: any) => {
    const newColumnsMap = { ...columnsMap };
    newColumnsMap[columnKey] = {
      ...newColumnsMap[columnKey],
      width: size.width,
    };
    setColumnsMap(newColumnsMap);
  };

  const tableColumns = React.useMemo(() => {
    const columnsSettingsMap = store.columnsSettings?.reduce?.((map: any, d: any) => {
      map[d.key] = d;
      return map;
    }, {}) || {};
    return columns
      .map((d: any, i: any) => {
        d ??= {};
        if (d == Table.EXPAND_COLUMN) return d;
        const columnKey = getColumnKey(d);
        // 给可展开的表头字段添加操作图标及样式
        let expandColumn: any = {};
        if (d.children?.length && d.hideInSettings !== true) {
          const columnSettings = columnsSettingsMap[d.key ?? d.dataIndex];
          let isExpand = (columnSettings?.expand ?? d.expand) === true;
          let isHideExpend = (columnSettings?.hideExpend ?? d.hideExpend) === true;
          expandColumn = isHideExpend ? null : {
            title: <>
              {d.title}
              <Button
                size='small'
                className='inlines bg-transparent border-0 shadow-none'
                onClick={() => store.changeExpandColumn(d.key || d.dataIndex)}
                icon={isExpand ? <MinusSquareOutlined /> : <PlusSquareOutlined />} />
            </>,
            className: `${d?.className?.replace?.(/text-center|text-right/g, '')} ${isExpand ? 'text-center' : 'text-right'}`,
            onHeaderCell: () => ({
              style: isExpand ? {
                width: `${d.width}px`,
                backgroundColor: '#FCF2DF',
                padding: '8px 12px',
                borderRight: '1px solid rgba(0,0,0,.05)',
              } : {},
            }),
          };
        }
        return {
          ...d,
          ...expandColumn,
          width: columnsMap[columnKey]?.width || d.width,
          sortIndex: i,
          onHeaderCell: (column: any) => Object.assign(
            d?.onHeaderCell?.(column) || {},
            expandColumn?.onHeaderCell?.(column) || {},
            {
              width: column.width,
              onResize: handleResize(getColumnKey(column)),
              disabledResize: column.disabledResize,
            }),
        };
      })
      .filter((d: any) => {
        const columnSettings = columnsSettingsMap[d.key ?? d.dataIndex];
        if (d.children?.length && d.hideInSettings !== true) {
          if ((columnSettings?.expand ?? d.expand) === true) {
            d.children = d.children.filter((o: any) => {
              const childColumnSettings = columnsSettingsMap[o.key ?? o.dataIndex];
              return o.hideInSettings === true || o.fixed || (childColumnSettings?.visible ?? o.visible) !== false;
            });
          } else {
            d.children = [];
          }
        }
        return d.hideInSettings === true || d.fixed || (columnSettings?.visible ?? d.visible) !== false;
      })
      .sort((a, b) => {
        const getColumnSortIndex = (column: any) => {
          const columnSettings = columnsSettingsMap[column.key ?? column.dataIndex];
          return column.fixed == 'left'
            ? -1
            : column.fixed == 'right'
              ? Math.pow(10, 8)
              : (isNumber(columnSettings?.sortIndex) ? columnSettings.sortIndex : column.sortIndex);
        };
        return getColumnSortIndex(a) - getColumnSortIndex(b);
      });
  }, [columns, columnsMap, store.columnsSettings]);
  /** 首次加载 */
  React.useEffect(() => {
    if (store.serverMode && (!deferLoading || store._isRequestBeforeMounted)) {
      store.request();
    }
  }, []);

  /** 分页悬浮 */
  // const tableSticky = React.useMemo(() => {
  //   let stickySettings: any = {};
  //   if (isObject(sticky)) {
  //     stickySettings = {
  //       ...sticky,
  //     };
  //   }
  //   if (pagination !== false) {
  //     stickySettings.offsetScroll = 64;
  //   }
  //   return stickySettings;
  // }, [sticky, pagination]);

  // if (sticky && pagination !== false) {
  //   tableControlledProps.pagination = {
  //     ...(isObject(tableControlledProps?.pagination) ? tableControlledProps?.pagination : null),
  //     className: classNames({ 'table-pagination-sticky': sticky }),
  //   };
  // }

  return <Table<RecordType>
    scroll={scroll}
    components={tableComponents}
    columns={tableColumns as ColumnsType<RecordType>}
    {...tableControlledProps}
    {...rest}
    // {...(sticky ? { sticky: tableSticky } : null)}
    ref={ref}
  />;
}));



type DataTableType = typeof DataTable;

interface InternalDataTableInterface extends DataTableType {
  useTable: typeof useTable;
  EXPAND_COLUMN: typeof Table.EXPAND_COLUMN;
  SELECTION_COLUMN: typeof Table.SELECTION_COLUMN;
  ColumnSettingsPopover: typeof ColumnSettingsPopover;
}

const InternalDataTable = DataTable as InternalDataTableInterface;
InternalDataTable.useTable = useTable;
InternalDataTable.EXPAND_COLUMN = Table.EXPAND_COLUMN;
InternalDataTable.SELECTION_COLUMN = Table.SELECTION_COLUMN;
InternalDataTable.displayName = 'DataTable';
InternalDataTable.ColumnSettingsPopover = ColumnSettingsPopover;


export default InternalDataTable;
